import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Grid, Alert, CircularProgress, Chip, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Select, MenuItem, IconButton, Tooltip, LinearProgress, TablePagination,
  Dialog, DialogTitle, DialogContent, DialogActions, Accordion, AccordionSummary,
  AccordionDetails, FormControl, InputLabel,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  FaFileExcel, FaImages, FaHistory, FaTable, FaTrash, FaPlus, FaDownload,
  FaCloudUploadAlt, FaSync, FaPlug, FaCheckCircle, FaTimesCircle, FaExclamationTriangle,
} from 'react-icons/fa';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InterfaceHeader from '../components/InterfaceHeader';
import API_BASE_URL from '../config/api';
import { TOOL_COLORS } from '../constants/colors';

const LINK_COLOR = TOOL_COLORS.LINK.primary;
const API = `${API_BASE_URL}/api/WayfairAssetUpload`;

// Suppliers we can upload for. Wayfair supplier IDs: Safavieh 381, Augusta
// Home 390 (sandbox-capable). SPNs differ: Safavieh = numeric UPC-style code
// (mapped from ItemID), Augusta = the manufacturer part number itself.
const SUPPLIERS = [
  { key: 'safavieh', name: 'Safavieh', supplierId: '381', blurb: 'SPNs are numeric UPC-style codes — filenames resolve via the SPN mapping.' },
  { key: 'augusta', name: 'Augusta Home', supplierId: '390', blurb: 'SPNs equal the manufacturer part number (e.g. MS431116).' },
];

const OUTCOME_COLORS = {
  ACCEPTED: '#16A34A',
  REJECTED: '#DC2626',
  RETRYABLE: '#D97706',
  SUBMITTED: '#2563EB',
  PENDING: '#64748B',
  SKIPPED: '#94A3B8',
};

const ROW_STATUS_INFO = {
  OK: { color: '#16A34A', label: 'OK' },
  EMPTY_SPN: { color: '#DC2626', label: 'Missing SPN' },
  SPN_NOT_FOUND: { color: '#D97706', label: 'SPN not found — item may not exist on Wayfair yet' },
  BAD_URL: { color: '#DC2626', label: 'URL invalid or unreachable' },
  DUPLICATE: { color: '#94A3B8', label: 'Duplicate row (skipped on submit)' },
  S3_CONFLICT: { color: '#D97706', label: 'A DIFFERENT image with this name already exists in S3 — the existing one was kept and will be used. Rename your file to upload the new version.' },
  S3_RENAMED: { color: '#0EA5E9', label: 'A different image already had this name in S3 — your new image was uploaded under a deduplicated name and is used for this row. The existing image was not touched.' },
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('jwtToken')}` });

const WayfairAssetUpload = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [supplier, setSupplier] = useState('safavieh');
  const [tab, setTab] = useState(0); // 0 Excel | 1 Images | 2 History | 3 Mappings
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  // Health check
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  // Active job (review grid or live results)
  const [job, setJob] = useState(null);
  const [rowEdits, setRowEdits] = useState({});       // id -> {spn,imageUrl,mediaType}
  const [deletedRows, setDeletedRows] = useState(new Set());
  const [savingRows, setSavingRows] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Excel mode
  const excelInputRef = useRef(null);
  const [excelUploading, setExcelUploading] = useState(false);

  // Images mode
  const filesInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // {done,total}
  const [uploadWarnings, setUploadWarnings] = useState([]);

  // History
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  // Mappings
  const [mappings, setMappings] = useState([]);
  const [mappingUploading, setMappingUploading] = useState('');
  const mappingInputRefs = { safavieh: useRef(null), augusta: useRef(null) };

  const pollRef = useRef(null);

  // ── Fetch helpers ─────────────────────────────────────────────────────
  const fetchJob = useCallback(async (id) => {
    const res = await fetch(`${API}/jobs/${id}`, { headers: authHeaders() });
    const data = await res.json();
    if (data.success) setJob(data.job);
    return data.job;
  }, []);

  // Open a different job (History → Open): clear grid state that belongs to
  // the previous job — stale pagination shows an empty page, stale edits /
  // deletions would corrupt the newly opened job's rows.
  const openJob = useCallback(async (id) => {
    setRowEdits({});
    setDeletedRows(new Set());
    setPage(0);
    await fetchJob(id);
  }, [fetchJob]);

  const fetchJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res = await fetch(`${API}/jobs`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setJobs(data.jobs);
    } catch (e) { /* noop */ }
    setJobsLoading(false);
  }, []);

  const fetchMappings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/mappings`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setMappings(data.mappings);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { if (tab === 2) fetchJobs(); if (tab === 3) fetchMappings(); }, [tab, fetchJobs, fetchMappings]);

  // Live polling while a job is mid-flight. Stops on DONE.
  useEffect(() => {
    if (job && (job.state === 'SUBMITTING' || job.state === 'POLLING')) {
      if (!pollRef.current) {
        pollRef.current = setInterval(() => fetchJob(job.id).catch(() => {}), 5000);
      }
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [job, fetchJob]);

  // ── Health ────────────────────────────────────────────────────────────
  const checkHealth = async () => {
    setHealthLoading(true);
    setHealth(null);
    try {
      const res = await fetch(`${API}/health/${supplier}`, { headers: authHeaders() });
      const data = await res.json();
      setHealth(data);
    } catch (e) {
      setHealth({ success: false, error: String(e) });
    }
    setHealthLoading(false);
  };

  // ── Excel import ──────────────────────────────────────────────────────
  const handleExcelUpload = async (file) => {
    if (!file) return;
    setError(''); setInfo(''); setExcelUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('supplier', supplier);
      const res = await fetch(`${API}/jobs/excel`, { method: 'POST', headers: authHeaders(), body: fd });
      const data = await res.json();
      if (!data.success) { setError(data.message || data.error || 'Import failed'); }
      else {
        setJob(data.job);
        setRowEdits({}); setDeletedRows(new Set()); setPage(0);
        setInfo(`Imported ${data.job.totalRows} rows — review below, then submit.`);
      }
    } catch (e) { setError(String(e)); }
    setExcelUploading(false);
    if (excelInputRef.current) excelInputRef.current.value = '';
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await fetch(`${API}/template`, { headers: authHeaders() });
      if (!res.ok || (res.headers.get('content-type') || '').includes('json')) {
        setError(`Template download failed (HTTP ${res.status})`);
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (res.headers.get('content-disposition') || '').match(/filename="?([^";]+)"?/)?.[1] || 'Wayfair Asset Upload Template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { setError(String(e)); }
  };

  // ── Image / folder upload (chunked) ───────────────────────────────────
  const handleImagesSelected = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setError(''); setInfo(''); setUploadWarnings([]);
    setImageUploading(true);

    // Chunk: ≤20 files and ≤80 MB per request so EB/IIS limits stay safe.
    const chunks = [];
    let current = [], currentBytes = 0;
    for (const f of files) {
      if (current.length >= 20 || (currentBytes + f.size) > 80 * 1024 * 1024) {
        if (current.length) chunks.push(current);
        current = []; currentBytes = 0;
      }
      current.push(f); currentBytes += f.size;
    }
    if (current.length) chunks.push(current);

    setUploadProgress({ done: 0, total: files.length });
    let jobId = 0;
    const warnings = [];
    try {
      for (const chunk of chunks) {
        const fd = new FormData();
        chunk.forEach((f) => fd.append('files', f));
        fd.append('supplier', supplier);
        fd.append('jobId', String(jobId));
        const res = await fetch(`${API}/jobs/images`, { method: 'POST', headers: authHeaders(), body: fd });
        const data = await res.json();
        if (!data.success) throw new Error(data.message || data.error || 'Upload failed');
        jobId = data.job.id;
        setJob(data.job);
        if (data.warnings?.length) warnings.push(...data.warnings);
        setUploadProgress((p) => ({ done: Math.min(p.done + chunk.length, p.total), total: p.total }));
      }
      setRowEdits({}); setDeletedRows(new Set()); setPage(0);
      setUploadWarnings(warnings);
      setInfo(`Uploaded ${files.length} file(s) to S3 — review the derived rows below, then submit.`);
    } catch (e) { setError(String(e.message || e)); }
    setImageUploading(false);
    setUploadProgress(null);
    if (filesInputRef.current) filesInputRef.current.value = '';
    if (folderInputRef.current) folderInputRef.current.value = '';
  };

  // ── Review grid edits ─────────────────────────────────────────────────
  const editRow = (id, field, value) =>
    setRowEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const effectiveRow = (row) => ({ ...row, ...(rowEdits[row.id] || {}) });

  // Returns true on success — submitJob aborts when the pre-submit save
  // fails, otherwise stale row values would be sent to Wayfair.
  const saveRows = async () => {
    if (!job) return false;
    setSavingRows(true); setError('');
    let ok = false;
    try {
      const edits = [];
      Object.entries(rowEdits).forEach(([id, e]) => {
        const base = job.rows.find((r) => r.id === Number(id));
        if (!base) return;
        edits.push({ id: Number(id), spn: e.spn ?? base.spn, imageUrl: e.imageUrl ?? base.imageUrl, mediaType: e.mediaType ?? base.mediaType, derivedMpn: e.derivedMpn ?? base.derivedMpn, deleted: false });
      });
      deletedRows.forEach((id) => edits.push({ id, deleted: true }));
      if (edits.length === 0) { setSavingRows(false); return true; }
      const res = await fetch(`${API}/jobs/${job.id}/rows`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: edits }),
      });
      const data = await res.json();
      if (!data.success) setError(data.message || data.error || 'Save failed');
      else { setJob(data.job); setRowEdits({}); setDeletedRows(new Set()); setInfo('Changes saved.'); ok = true; }
    } catch (e) { setError(String(e)); }
    setSavingRows(false);
    return ok;
  };

  const hasUnsaved = Object.keys(rowEdits).length > 0 || deletedRows.size > 0;

  const [resolving, setResolving] = useState(false);

  // Save any pending ItemID edits, then ask the backend to look up SPNs for
  // every row that still has none (Mode B). Useful after correcting a
  // mis-derived ItemID.
  const resolveSpns = async () => {
    if (!job) return;
    setResolving(true); setError('');
    try {
      if (hasUnsaved) {
        const saved = await saveRows();
        if (!saved) { setResolving(false); return; }
      }
      const res = await fetch(`${API}/jobs/${job.id}/resolve-spns`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!data.success) setError(data.message || data.error || 'SPN lookup failed');
      else {
        setJob(data.job); setRowEdits({});
        setInfo(
          data.checkedCount === 0
            ? 'No rows needed an SPN lookup.'
            : `Resolved ${data.resolved} of ${data.checkedCount} SPN${data.checkedCount === 1 ? '' : 's'}.${data.resolved < data.checkedCount ? ' The rest still have no match — fix the ItemID and try again.' : ''}`
        );
      }
    } catch (e) { setError(String(e)); }
    setResolving(false);
  };

  const submitJob = async () => {
    setConfirmSubmitOpen(false);
    if (!job) return;
    setSubmitting(true); setError('');
    try {
      if (hasUnsaved) {
        const saved = await saveRows();
        if (!saved) {
          // Don't submit stale values — the save error is already displayed.
          setSubmitting(false);
          return;
        }
      }
      const res = await fetch(`${API}/jobs/${job.id}/submit`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!data.success) setError(data.message || data.error || 'Submit failed');
      else { await fetchJob(job.id); setInfo('Submitted — Wayfair processes asynchronously; results appear below as they land.'); }
    } catch (e) { setError(String(e)); }
    setSubmitting(false);
  };

  const retryFailed = async () => {
    if (!job) return;
    setError('');
    try {
      const res = await fetch(`${API}/jobs/${job.id}/retry-failed`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!data.success) setError(data.message || data.error || 'Retry failed');
      else { await fetchJob(job.id); setInfo(`Retrying ${data.retrying} row(s)...`); }
    } catch (e) { setError(String(e)); }
  };

  const repoll = async () => {
    if (!job) return;
    setError('');
    try {
      const res = await fetch(`${API}/jobs/${job.id}/repoll`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!data.success) setError(data.message || data.error || 'Re-poll failed');
      else { await fetchJob(job.id); setInfo('Re-polling Wayfair for outstanding results...'); }
    } catch (e) { setError(String(e)); }
  };

  const downloadReport = async () => {
    if (!job) return;
    try {
      const res = await fetch(`${API}/jobs/${job.id}/report`, { headers: authHeaders() });
      // Never save an error response as a fake .xlsx — surface it instead.
      if (!res.ok || (res.headers.get('content-type') || '').includes('json')) {
        let msg = `Report download failed (HTTP ${res.status})`;
        try { const j = await res.json(); msg = j.message || j.error || msg; } catch { /* keep msg */ }
        setError(msg);
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (res.headers.get('content-disposition') || '').match(/filename="?([^";]+)"?/)?.[1] || `Wayfair Job ${job.id} Report.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { setError(String(e)); }
  };

  // ── Mapping upload ────────────────────────────────────────────────────
  const handleMappingUpload = async (supplierKey, file) => {
    if (!file) return;
    setMappingUploading(supplierKey); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/mappings/${supplierKey}`, { method: 'POST', headers: authHeaders(), body: fd });
      const data = await res.json();
      if (!data.success) setError(data.message || data.error || 'Mapping upload failed');
      else { setInfo(`Mapping refreshed for ${supplierKey}: ${data.rowCount.toLocaleString()} rows.`); fetchMappings(); }
    } catch (e) { setError(String(e)); }
    setMappingUploading('');
    const ref = mappingInputRefs[supplierKey];
    if (ref?.current) ref.current.value = '';
  };

  const handleMappingDownload = async (supplierKey) => {
    try {
      const res = await fetch(`${API}/mappings/${supplierKey}/download`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success && data.url) window.open(data.url, '_blank');
      else setError(data.message || 'No mapping file available');
    } catch (e) { setError(String(e)); }
  };

  // ── Render helpers ────────────────────────────────────────────────────
  const renderSupplierPicker = () => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {SUPPLIERS.map((s) => {
        const selected = supplier === s.key;
        return (
          <Grid item xs={12} sm={6} key={s.key}>
            <Paper
              onClick={() => { setSupplier(s.key); setHealth(null); }}
              elevation={0}
              sx={{
                p: 2, cursor: 'pointer', borderRadius: 2,
                border: `2px solid ${selected ? LINK_COLOR : theme.palette.divider}`,
                bgcolor: selected ? alpha(LINK_COLOR, isDark ? 0.12 : 0.06) : 'transparent',
                transition: 'all .15s',
                '&:hover': { borderColor: LINK_COLOR },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{s.name}</Typography>
                <Chip label={`Supplier ${s.supplierId}`} size="small" sx={{ height: 20, fontSize: 11 }} />
                {s.key === 'augusta' && health?.useSandbox && supplier === 'augusta' && (
                  <Chip label="SANDBOX" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: '#D9770620', color: '#D97706' }} />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">{s.blurb}</Typography>
            </Paper>
          </Grid>
        );
      })}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" startIcon={healthLoading ? <CircularProgress size={14} /> : <FaPlug size={13} />}
            onClick={checkHealth} disabled={healthLoading}
            sx={{ textTransform: 'none', borderColor: LINK_COLOR, color: LINK_COLOR }}>
            Test connection
          </Button>
          {health && !health.configured && (
            <Chip icon={<FaTimesCircle size={13} />} label="Not configured — Wayfair credentials missing" size="small" sx={{ bgcolor: '#DC262620', color: '#DC2626' }} />
          )}
          {health?.configured && (health.clients || []).map((c, i) => (
            <Tooltip key={i} title={c.ok ? (c.scopes || []).join(', ') : c.error} arrow>
              <Chip
                icon={c.ok ? <FaCheckCircle size={13} /> : <FaTimesCircle size={13} />}
                label={`${c.label}: ${c.ok ? `OK${c.canWrite ? ' · write' : ''}${c.canRead ? ' · read' : ''}` : 'FAILED'}`}
                size="small"
                sx={{ bgcolor: c.ok ? '#16A34A20' : '#DC262620', color: c.ok ? '#16A34A' : '#DC2626' }}
              />
            </Tooltip>
          ))}
          {health?.configured && health.useSandbox !== undefined && health.supplier === 'augusta' && (
            <Typography variant="caption" color="text.secondary">
              Environment: {health.useSandbox ? 'Sandbox (safe testing)' : 'PRODUCTION'}
            </Typography>
          )}
        </Box>
      </Grid>
    </Grid>
  );

  const renderReviewGrid = () => {
    if (!job) return null;
    const isDraft = job.state === 'DRAFT';
    const visibleRows = job.rows.filter((r) => !deletedRows.has(r.id));
    const pagedRows = visibleRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    const problemCount = visibleRows.filter((r) => effectiveRow(r).rowStatus !== 'OK' && r.rowStatus !== 'DUPLICATE').length;
    const submittable = visibleRows.filter((r) => r.rowStatus !== 'DUPLICATE' && (effectiveRow(r).spn || '').trim() && (effectiveRow(r).imageUrl || '').trim()).length;

    return (
      <Paper elevation={0} sx={{ p: 2.5, mt: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 17 }}>
            {isDraft ? 'Review & Edit' : 'Results'} — Job #{job.id}
          </Typography>
          <Chip label={job.state} size="small" sx={{
            fontWeight: 700, height: 22,
            bgcolor: alpha(job.state === 'DONE' ? '#16A34A' : job.state === 'DRAFT' ? '#64748B' : LINK_COLOR, 0.15),
            color: job.state === 'DONE' ? '#16A34A' : job.state === 'DRAFT' ? '#64748B' : LINK_COLOR,
          }} />
          <Chip label={`${job.supplier === 'augusta' ? 'Augusta Home' : 'Safavieh'}${job.useSandbox ? ' · sandbox' : ''}`} size="small" sx={{ height: 22 }} />
          <Chip label={`${visibleRows.length} rows`} size="small" sx={{ height: 22 }} />
          {isDraft && problemCount > 0 && (
            <Chip icon={<FaExclamationTriangle size={12} />} label={`${problemCount} row(s) flagged`} size="small"
              sx={{ height: 22, bgcolor: '#D9770620', color: '#D97706' }} />
          )}
          <Box sx={{ flex: 1 }} />
          {(job.state === 'SUBMITTING' || job.state === 'POLLING') && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} sx={{ color: LINK_COLOR }} />
              <Typography variant="caption" color="text.secondary">
                {job.state === 'SUBMITTING' ? 'Submitting batches to Wayfair…' : 'Waiting for Wayfair results…'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Outcome summary once submitted */}
        {job.state !== 'DRAFT' && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Chip label={`✓ Accepted ${job.acceptedCount}`} size="small" sx={{ bgcolor: '#16A34A20', color: '#16A34A', fontWeight: 700 }} />
            <Chip label={`✗ Rejected ${job.rejectedCount}`} size="small" sx={{ bgcolor: '#DC262620', color: '#DC2626', fontWeight: 700 }} />
            {job.pendingCount > 0 && <Chip label={`⏳ Pending ${job.pendingCount}`} size="small" sx={{ bgcolor: '#2563EB20', color: '#2563EB', fontWeight: 700 }} />}
            {job.errorMessage && <Chip label={job.errorMessage} size="small" sx={{ bgcolor: '#DC262610', color: '#DC2626' }} />}
          </Box>
        )}

        {/* Error-code grouping for finished jobs */}
        {job.state === 'DONE' && job.rejectedCount > 0 && (() => {
          const grouped = {};
          job.rows.filter((r) => r.outcome === 'REJECTED' || r.outcome === 'RETRYABLE').forEach((r) => {
            const k = r.errorCode || 'UNKNOWN';
            grouped[k] = grouped[k] || { title: r.errorTitle, detail: r.errorDetail, rows: [] };
            grouped[k].rows.push(r);
          });
          return (
            <Box sx={{ mb: 1.5 }}>
              {Object.entries(grouped).map(([code, g]) => (
                <Accordion key={code} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#DC2626' }}>{code}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      {g.title} — {g.rows.length} row(s)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>{g.detail}</Typography>
                    <Box sx={{ mt: 1 }}>
                      {g.rows.slice(0, 20).map((r) => (
                        <Typography key={r.id} variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>
                          {r.spn} — {r.imageUrl}
                        </Typography>
                      ))}
                      {g.rows.length > 20 && <Typography variant="caption" color="text.secondary">…and {g.rows.length - 20} more (see report)</Typography>}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          );
        })()}

        {/* Action bar */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          {isDraft && (
            <>
              <Button variant="contained" disabled={submitting || submittable === 0}
                onClick={() => setConfirmSubmitOpen(true)}
                startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <FaCloudUploadAlt size={14} />}
                sx={{ textTransform: 'none', bgcolor: LINK_COLOR, '&:hover': { bgcolor: TOOL_COLORS.LINK.hover } }}>
                Submit {submittable} row(s) to Wayfair
              </Button>
              <Button variant="outlined" disabled={!hasUnsaved || savingRows} onClick={saveRows}
                startIcon={savingRows ? <CircularProgress size={14} /> : null}
                sx={{ textTransform: 'none' }}>
                Save changes{hasUnsaved ? ` (${Object.keys(rowEdits).length + deletedRows.size})` : ''}
              </Button>
              {job.mode === 'images' && (
                <Button variant="outlined" disabled={resolving} onClick={resolveSpns}
                  startIcon={resolving ? <CircularProgress size={14} /> : <FaSync size={13} />}
                  sx={{ textTransform: 'none' }}>
                  {resolving ? 'Looking up…' : 'Find SPNs'}
                </Button>
              )}
              <Button variant="outlined" size="small" sx={{ textTransform: 'none' }} startIcon={<FaPlus size={11} />}
                onClick={async () => {
                  const res = await fetch(`${API}/jobs/${job.id}/rows`, {
                    method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ rows: [{ id: 0, spn: '', imageUrl: '', mediaType: 'IMAGE', deleted: false }] }),
                  });
                  const data = await res.json();
                  if (data.success) setJob(data.job);
                }}>
                Add row
              </Button>
            </>
          )}
          {job.state === 'DONE' && (job.rows.some((r) => r.outcome === 'RETRYABLE') ||
            job.rows.some((r) => r.outcome === 'PENDING') ||
            job.rows.some((r) => r.outcome === 'REJECTED' && (!r.errorCode || ['UNABLE_TO_VERIFY_PERMISSIONS', 'SUBMIT_FAILED'].includes(r.errorCode)))) && (
            <Button variant="outlined" onClick={retryFailed} startIcon={<FaSync size={13} />} sx={{ textTransform: 'none', color: '#D97706', borderColor: '#D97706' }}>
              Retry failed
            </Button>
          )}
          {job.state === 'DONE' && job.rows.some((r) => r.outcome === 'SUBMITTED') && (
            <Button variant="outlined" onClick={repoll} startIcon={<FaSync size={13} />} sx={{ textTransform: 'none' }}>
              Re-poll pending
            </Button>
          )}
          <Button variant="outlined" onClick={downloadReport} startIcon={<FaDownload size={13} />} sx={{ textTransform: 'none' }}>
            Download {isDraft ? 'as Excel' : 'report'}
          </Button>
        </Box>

        {/* Grid */}
        <TableContainer sx={{ maxHeight: 560, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: 190 }}>SPN</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Image URL</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 105 }}>Media</TableCell>
                {job.mode === 'images' && (
                  <TableCell sx={{ fontWeight: 700, width: 170 }}>
                    {job.supplier === 'safavieh' ? 'ItemID' : 'Derived from'}
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 700, width: 170 }}>{isDraft ? 'Check' : 'Outcome'}</TableCell>
                {isDraft && <TableCell sx={{ width: 44 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map((row) => {
                const r = effectiveRow(row);
                const statusInfo = ROW_STATUS_INFO[r.rowStatus] || ROW_STATUS_INFO.OK;
                return (
                  <TableRow key={row.id} hover sx={{ opacity: row.rowStatus === 'DUPLICATE' ? 0.55 : 1 }}>
                    <TableCell>
                      {isDraft ? (
                        <TextField variant="standard" size="small" fullWidth value={r.spn || ''}
                          onChange={(e) => editRow(row.id, 'spn', e.target.value)}
                          InputProps={{ sx: { fontSize: 13, fontFamily: 'monospace' } }} />
                      ) : (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 13 }}>{r.spn}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 420 }}>
                      {isDraft ? (
                        <TextField variant="standard" size="small" fullWidth value={r.imageUrl || ''}
                          onChange={(e) => editRow(row.id, 'imageUrl', e.target.value)}
                          InputProps={{ sx: { fontSize: 12 } }} />
                      ) : (
                        <Typography variant="body2" noWrap sx={{ fontSize: 12 }}>
                          <a href={r.imageUrl} target="_blank" rel="noreferrer" style={{ color: LINK_COLOR }}>{r.imageUrl}</a>
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isDraft ? (
                        <Select variant="standard" size="small" value={r.mediaType || 'IMAGE'}
                          onChange={(e) => editRow(row.id, 'mediaType', e.target.value)} sx={{ fontSize: 13 }}>
                          <MenuItem value="IMAGE">IMAGE</MenuItem>
                          <MenuItem value="VIDEO">VIDEO</MenuItem>
                        </Select>
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: 13 }}>{r.mediaType}</Typography>
                      )}
                    </TableCell>
                    {job.mode === 'images' && (
                      <TableCell>
                        {isDraft ? (
                          <Tooltip title={`File: ${row.sourceFilename || '—'} — edit the ItemID, then "Find SPNs" to look it up`} arrow disableInteractive>
                            <TextField variant="standard" size="small" fullWidth value={r.derivedMpn || ''}
                              onChange={(e) => editRow(row.id, 'derivedMpn', e.target.value)}
                              InputProps={{ sx: { fontSize: 13, fontFamily: 'monospace' } }} />
                          </Tooltip>
                        ) : (
                          <Tooltip title={`File: ${row.sourceFilename || '—'}`} arrow disableInteractive>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{row.derivedMpn || '—'}</Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {isDraft ? (
                        <Tooltip title={statusInfo.label} arrow disableInteractive>
                          <Chip label={r.rowStatus} size="small"
                            sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: alpha(statusInfo.color, 0.15), color: statusInfo.color }} />
                        </Tooltip>
                      ) : (
                        <Tooltip title={row.errorCode ? `${row.errorCode}: ${row.errorTitle || ''} ${row.errorDetail || ''}` : ''} arrow disableInteractive>
                          <Chip label={row.outcome + (row.errorCode ? ` · ${row.errorCode}` : '')} size="small"
                            sx={{ height: 20, fontSize: 10.5, fontWeight: 700, maxWidth: 220,
                              bgcolor: alpha(OUTCOME_COLORS[row.outcome] || '#64748B', 0.15),
                              color: OUTCOME_COLORS[row.outcome] || '#64748B' }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    {isDraft && (
                      <TableCell>
                        <IconButton size="small" onClick={() => setDeletedRows((p) => new Set([...p, row.id]))}>
                          <FaTrash size={12} color="#DC2626" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={visibleRows.length}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 250]}
        />
      </Paper>
    );
  };

  // ── Page ──────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto', position: 'relative' }}>
        <InterfaceHeader title="Wayfair Asset Upload" color={LINK_COLOR} category="link" />

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
        {info && <Alert severity="info" onClose={() => setInfo('')} sx={{ mb: 2 }}>{info}</Alert>}
        {uploadWarnings.length > 0 && (
          <Alert severity="warning" onClose={() => setUploadWarnings([])} sx={{ mb: 2 }}>
            {uploadWarnings.slice(0, 5).map((w, i) => <div key={i}>{w.file}: {w.message}</div>)}
            {uploadWarnings.length > 5 && <div>…and {uploadWarnings.length - 5} more overwrites</div>}
          </Alert>
        )}

        {renderSupplierPicker()}

        <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
            '& .Mui-selected': { color: `${LINK_COLOR} !important` },
            '& .MuiTabs-indicator': { bgcolor: LINK_COLOR },
          }}>
            <Tab icon={<FaFileExcel size={14} />} iconPosition="start" label="Excel Import" />
            <Tab icon={<FaImages size={14} />} iconPosition="start" label="Image / Folder Upload" />
            <Tab icon={<FaHistory size={14} />} iconPosition="start" label="Job History" />
            <Tab icon={<FaTable size={14} />} iconPosition="start" label="SPN Mappings" />
          </Tabs>

          <Box sx={{ p: 2.5 }}>
            {tab === 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Upload an .xlsx with <b>SupplierPartNumber</b> and <b>ImageUrl</b> columns (optional <b>MediaType</b> — IMAGE/VIDEO,
                  inferred from the file extension when blank). Rows land in a review grid before anything is sent to Wayfair.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <input type="file" accept=".xlsx" hidden ref={excelInputRef}
                    onChange={(e) => handleExcelUpload(e.target.files?.[0])} />
                  <Button variant="contained" disabled={excelUploading}
                    startIcon={excelUploading ? <CircularProgress size={14} color="inherit" /> : <FaFileExcel size={14} />}
                    onClick={() => excelInputRef.current?.click()}
                    sx={{ textTransform: 'none', bgcolor: LINK_COLOR, '&:hover': { bgcolor: TOOL_COLORS.LINK.hover } }}>
                    {excelUploading ? 'Parsing…' : 'Upload Excel'}
                  </Button>
                  <Button variant="outlined" onClick={handleDownloadTemplate} startIcon={<FaDownload size={13} />} sx={{ textTransform: 'none' }}>
                    Download Template
                  </Button>
                </Box>
              </Box>
            )}

            {tab === 1 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Pick image files or an entire folder. Each file is staged in the public Wayfair S3 bucket
                  (<code>safaviehwayfairimages/Original/</code>), {supplier === 'safavieh'
                    ? <>the <b>ItemID</b> is derived from the filename (image-type suffixes like <code>-ROOMAI</code>, <code>-FRONT-NS</code> are stripped)
                      and the numeric SPN is looked up from the Safavieh SPN mapping — rows whose ItemID has no match keep a blank SPN you can fill in manually</>
                    : <>the part number is derived from the filename (image-type suffixes stripped) and used as the SPN directly (Augusta)</>}.
                  Nothing goes to Wayfair until you review and submit.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <input type="file" accept="image/*,video/*" multiple hidden ref={filesInputRef}
                    onChange={(e) => handleImagesSelected(e.target.files)} />
                  {/* webkitdirectory: Chromium folder picker */}
                  <input type="file" webkitdirectory="" directory="" multiple hidden ref={folderInputRef}
                    onChange={(e) => handleImagesSelected(e.target.files)} />
                  <Button variant="contained" disabled={imageUploading}
                    startIcon={imageUploading ? <CircularProgress size={14} color="inherit" /> : <FaImages size={14} />}
                    onClick={() => filesInputRef.current?.click()}
                    sx={{ textTransform: 'none', bgcolor: LINK_COLOR, '&:hover': { bgcolor: TOOL_COLORS.LINK.hover } }}>
                    Select files
                  </Button>
                  <Button variant="outlined" disabled={imageUploading}
                    onClick={() => folderInputRef.current?.click()} sx={{ textTransform: 'none' }}>
                    Select folder
                  </Button>
                </Box>
                {uploadProgress && (
                  <Box sx={{ mt: 2, maxWidth: 480 }}>
                    <Typography variant="caption" color="text.secondary">
                      Uploading to S3… {uploadProgress.done}/{uploadProgress.total} files
                    </Typography>
                    <LinearProgress variant="determinate" value={(uploadProgress.done / uploadProgress.total) * 100}
                      sx={{ borderRadius: 1, height: 8, '& .MuiLinearProgress-bar': { bgcolor: LINK_COLOR } }} />
                  </Box>
                )}
              </Box>
            )}

            {tab === 2 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Past uploads</Typography>
                  <IconButton size="small" onClick={fetchJobs}><FaSync size={13} /></IconButton>
                </Box>
                {jobsLoading ? <CircularProgress size={22} sx={{ color: LINK_COLOR }} /> : (
                  <TableContainer sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Supplier</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>State</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Rows</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>✓ / ✗ / ⏳</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>By</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Created (ET)</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {jobs.map((j) => (
                          <TableRow key={j.id} hover>
                            <TableCell>{j.id}</TableCell>
                            <TableCell>{j.supplier === 'augusta' ? 'Augusta Home' : 'Safavieh'}{j.useSandbox ? ' (sandbox)' : ''}</TableCell>
                            <TableCell>{j.mode}</TableCell>
                            <TableCell>
                              <Chip label={j.state} size="small" sx={{
                                height: 20, fontSize: 10.5, fontWeight: 700,
                                bgcolor: alpha(j.state === 'DONE' ? '#16A34A' : j.state === 'DRAFT' ? '#64748B' : LINK_COLOR, 0.15),
                                color: j.state === 'DONE' ? '#16A34A' : j.state === 'DRAFT' ? '#64748B' : LINK_COLOR,
                              }} />
                            </TableCell>
                            <TableCell>{j.totalRows}</TableCell>
                            <TableCell>
                              <span style={{ color: '#16A34A', fontWeight: 700 }}>{j.acceptedCount}</span>
                              {' / '}<span style={{ color: '#DC2626', fontWeight: 700 }}>{j.rejectedCount}</span>
                              {' / '}<span style={{ color: '#2563EB', fontWeight: 700 }}>{j.pendingCount}</span>
                            </TableCell>
                            <TableCell><Typography variant="caption">{j.createdByEmail}</Typography></TableCell>
                            <TableCell><Typography variant="caption">{j.createdAt}</Typography></TableCell>
                            <TableCell>
                              <Button size="small" sx={{ textTransform: 'none' }} onClick={() => openJob(j.id)}>
                                Open
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {jobs.length === 0 && (
                          <TableRow><TableCell colSpan={9}><Typography variant="caption" color="text.secondary">No jobs yet.</Typography></TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {tab === 3 && (
              <Grid container spacing={2}>
                {SUPPLIERS.map((s) => {
                  const meta = mappings.find((m) => m.supplier === s.key);
                  return (
                    <Grid item xs={12} sm={6} key={s.key}>
                      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>{s.name} — MPN → SPN catalog</Typography>
                        {meta ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {meta.rowCount.toLocaleString()} mappings · {meta.fileName}<br />
                            Updated {meta.uploadedAt} by {meta.uploadedByEmail}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            No mapping uploaded yet. {s.key === 'safavieh'
                              ? 'Upload "supplier part number to item.xlsx" — required for filename → SPN resolution.'
                              : 'Upload "supplier part number augusta home.xlsx" — used to validate derived SPNs.'}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <input type="file" accept=".xlsx" hidden ref={mappingInputRefs[s.key]}
                            onChange={(e) => handleMappingUpload(s.key, e.target.files?.[0])} />
                          <Button size="small" variant="contained" disabled={mappingUploading === s.key}
                            startIcon={mappingUploading === s.key ? <CircularProgress size={13} color="inherit" /> : <FaCloudUploadAlt size={13} />}
                            onClick={() => mappingInputRefs[s.key].current?.click()}
                            sx={{ textTransform: 'none', bgcolor: LINK_COLOR, '&:hover': { bgcolor: TOOL_COLORS.LINK.hover } }}>
                            Upload new version
                          </Button>
                          <Button size="small" variant="outlined" disabled={!meta?.fileS3Url}
                            startIcon={<FaDownload size={12} />} onClick={() => handleMappingDownload(s.key)}
                            sx={{ textTransform: 'none' }}>
                            Download current
                          </Button>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    The mapping powers Mode B (filename → SPN). Expected columns: <b>Supplier Part Number</b>, <b>Manufacturer Part Number</b>.
                    Uploading replaces the supplier's mapping entirely; the workbook is archived so "Download current" always returns the live version.
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>
        </Paper>

        {renderReviewGrid()}

        {/* Submit confirmation */}
        <Dialog open={confirmSubmitOpen} onClose={() => setConfirmSubmitOpen(false)}>
          <DialogTitle>Submit to Wayfair?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              This sends the media associations to the <b>{job?.supplier === 'augusta' ? 'Augusta Home' : 'Safavieh'}</b> catalog
              {job?.useSandbox ? ' (sandbox — safe)' : ' (LIVE production catalog)'}.
              Wayfair processes asynchronously; results usually land in 1–20 minutes.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmSubmitOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" onClick={submitJob} sx={{ textTransform: 'none', bgcolor: LINK_COLOR }}>
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default WayfairAssetUpload;
